// src/components/Investment/InvestmentCTA.jsx
import React, { useMemo, useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import "./InvestmentCTA.css";
import { useLanguage } from "../../contexts/LanguageContext";
import { FaUser, FaUserTie, FaWhatsapp, FaEnvelope, FaPencilAlt } from "react-icons/fa";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/firebase";

/* ---- Flag assets (static imports) ---- */
import aeFlag from "../../assets/images/flags/ae_flag.png";
import usFlag from "../../assets/images/flags/us_flag.png";
import frFlag from "../../assets/images/flags/fr_flag.png";
import lbFlag from "../../assets/images/flags/lb_flag.svg";
import qaFlag from "../../assets/images/flags/qa_flag.svg";
import saFlag from "../../assets/images/flags/saudi_flag.webp";
import syFlag from "../../assets/images/flags/syria_flag.webp";

/* Prefer ISO -> image, then currency code -> image */
const ISO_FLAG_MAP = {
  AE: aeFlag,
  US: usFlag,
  FR: frFlag,
  LB: lbFlag,
  QA: qaFlag,
  SA: saFlag,
  SY: syFlag,
};
const CODE_FLAG_MAP = {
  AED: aeFlag,
  USD: usFlag,
  EUR: frFlag,
  LBP: lbFlag,
  QAR: qaFlag,
  SAR: saFlag,
  SYP: syFlag,
};

/* normalize odd values */
const NORMALIZE_MAP = {
  AED: "AED", "UAE DIRHAM": "AED", "AED ": "AED", "د.إ": "AED",
  USD: "USD", "US DOLLAR": "USD", "U.S. DOLLAR": "USD", "US$": "USD", "$": "USD",
  EUR: "EUR", EURO: "EUR", "€": "EUR",
  QAR: "QAR", "QATARI RIYAL": "QAR",
  SAR: "SAR", RIYAL: "SAR", "SAUDI RIYAL": "SAR",
  LBP: "LBP", "LEBANESE POUND": "LBP", LIRA: "LBP",
  SYP: "SYP", "SYRIAN POUND": "SYP",
};
const normalizeCode = (v) => {
  if (!v) return "AED";
  const key = String(v).trim().toUpperCase();
  return NORMALIZE_MAP[key] || key;
};
const normalizeIso = (v) => (v ? String(v).trim().toUpperCase() : undefined);

/* choose the best flag for a row */
const flagFor = (code, iso) => {
  const isoKey = normalizeIso(iso);
  if (isoKey && ISO_FLAG_MAP[isoKey]) return ISO_FLAG_MAP[isoKey];
  const codeKey = normalizeCode(code);
  return CODE_FLAG_MAP[codeKey] || aeFlag;
};

// Base amounts in USD (default currency)
const baseAmountsUSD = [
  { id: "a10m", valueUSD: 10000000, equity: 20, returns: 80, color: "#3b82f6" },
  { id: "a5m",  valueUSD: 5000000,  equity: 10, returns: 40, color: "#8b5cf6" },
  { id: "a25m", valueUSD: 2500000,  equity: 5,  returns: 20, color: "#22c55e" },
  { id: "a1m",  valueUSD: 1000000,  equity: 2,  returns: 8,  color: "#f59e0b" },
  { id: "a500k",valueUSD: 500000,   equity: 1,  returns: 4,  color: "#ec4899" },
];

const InvestmentCTA = () => {
  const { translations: t } = useLanguage();

  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [type, setType] = useState("normal");
  const [currency, setCurrency] = useState("USD");
  const [selectedId, setSelectedId] = useState("");
  const [message, setMessage] = useState("");

  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dropdown state
  const [open, setOpen] = useState(false);
  const ddRef = useRef(null);

  useEffect(() => {
    const closeOnOutside = (e) => {
      if (!ddRef.current) return;
      if (!ddRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutside);
    return () => document.removeEventListener("mousedown", closeOnOutside);
  }, []);

  // Fetch, normalize and de-dupe currencies with exchange rates
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const ref = collection(db, "app", "currencies", "available");
        const snap = await getDocs(ref);
  
        const raw = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        const normalized = raw.map((r) => {
          const code = normalizeCode(r.code || r.name || r.id || r.currency || r.symbol);
          const iso  = (r.iso || "").toString().trim().toUpperCase();
          const name = r.name || code;
          const exchangeRate = r.USDToCoinExchangeRate || 1; // Default to 1 if not found
          return { code, iso, name, exchangeRate };
        });
  
        // de-dupe by code
        const dedup = Array.from(new Map(normalized.map(c => [c.code, c])).values());
  
        // ⛔️ exclude Winis / Winicoins variants
        const EXCLUDE = new Set(["WINIS", "WINICOINS", "WINICOIN", "WCOIN"]);
        const cleaned = dedup.filter(c => !EXCLUDE.has(c.code.toUpperCase()));
  
        // order like your UI (no Winis here) - USD first as base currency
        const ORDER = ["USD", "AED", "EUR", "LBP", "QAR", "SAR", "SYP"];
        cleaned.sort((a, b) => {
          const ai = ORDER.indexOf(a.code), bi = ORDER.indexOf(b.code);
          return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
        });
  
        setCurrencies(cleaned);
  
        // if current currency was excluded somehow, reset to USD or first available
        if (!cleaned.find(c => c.code === currency) && cleaned[0]) {
          const usdCurrency = cleaned.find(c => c.code === "USD");
          setCurrency(usdCurrency ? "USD" : cleaned[0].code);
        }
      } catch {
        // fallback without Winis - USD first as base currency
        setCurrencies([
          { code: "USD", iso: "US", name: "US Dollar", exchangeRate: 1 },
          { code: "AED", iso: "AE", name: "UAE Dirham", exchangeRate: 3.67 },
          { code: "EUR", iso: "FR", name: "Euro", exchangeRate: 0.85 },
          { code: "LBP", iso: "LB", name: "Lebanese Pound", exchangeRate: 89000 },
          { code: "QAR", iso: "QA", name: "Qatari Riyal", exchangeRate: 3.64 },
          { code: "SAR", iso: "SA", name: "Saudi Riyal", exchangeRate: 3.75 },
          { code: "SYP", iso: "SY", name: "Syrian Pound", exchangeRate: 13000 },
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchCurrencies();
  }, [currency]);

  // Convert amounts based on selected currency
  const list = useMemo(() => {
    const selectedCurrency = currencies.find(c => c.code === currency);
    const usdCurrency = currencies.find(c => c.code === "USD");
    
    if (!selectedCurrency || !usdCurrency) {
      // If currencies not loaded yet, return USD amounts as default
      return baseAmountsUSD.map(item => ({
        ...item,
        value: item.valueUSD,
        originalValueUSD: item.valueUSD
      }));
    }
    
    // Calculate conversion rate from USD to selected currency
    // USD to target currency = target exchange rate
    const usdToTargetRate = selectedCurrency.exchangeRate;
    
    return baseAmountsUSD.map(item => ({
      ...item,
      value: Math.round(item.valueUSD * usdToTargetRate),
      originalValueUSD: item.valueUSD
    }));
  }, [currency, currencies]);

  const selected = list.find((x) => x.id === selectedId) || list[2];

  const title = t.investment?.readyTitle || "Ready to invest in iWinto?";
  const subtitle =
    t.investment?.readySubtitle ||
    "Join our journey and become part of the next big success story.";

  // Handle form submission like Contact.jsx
  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.target;
    
    // Update hidden fields with current values before submission
    const hiddenFields = {
      'investor_type': type === "normal" ? "Normal Investor" : "Equity Partner",
      'currency': currency,
      'investment_amount': `${currency} ${selected.value.toLocaleString()}`,
      'equity_percentage': `${selected.equity}%`,
      'return_percentage': `${selected.returns}%`,
      '_subject': `🚀 Investment Request - ${name || "New Investor"} - ${currency} ${selected.value.toLocaleString()}`
    };

    // Update or create hidden fields
    Object.entries(hiddenFields).forEach(([name, value]) => {
      let field = form.querySelector(`input[name="${name}"]`);
      if (!field) {
        field = document.createElement('input');
        field.type = 'hidden';
        field.name = name;
        form.appendChild(field);
      }
      field.value = value;
    });

    // Submit the form
    form.submit();
  };

  const waText = encodeURIComponent(
`${t.investment?.form?.waMessage?.greeting || "Hello iWinto Investment Team,"}

${t.investment?.form?.waMessage?.intro || "I'm interested in investing in iWinto and would like to discuss investment opportunities."}

📋 ${t.investment?.form?.waMessage?.detailsTitle || "Investment Details"}:
• ${t.investment?.form?.waMessage?.nameLabel || "Name"}: ${name || "-"}
• ${t.investment?.form?.waMessage?.contactLabel || "Contact"}: ${contact || "-"}
• ${t.investment?.form?.waMessage?.typeLabel || "Investment Type"}: ${type === "normal" ? (t.investment?.form?.normalInvestor || "Normal Investor") : (t.investment?.form?.equityPartner || "Equity Partner")}
• ${t.investment?.form?.waMessage?.currencyLabel || "Preferred Currency"}: ${currency}
• ${t.investment?.form?.waMessage?.amountLabel || "Investment Amount"}: ${currency} ${selected.value.toLocaleString()}
• ${t.investment?.form?.waMessage?.equityLabel || "Equity"}: ${selected.equity}%
• ${t.investment?.form?.waMessage?.returnLabel || "Expected Return"}: ${selected.returns}%

💬 ${t.investment?.form?.waMessage?.additionalInfoLabel || "Additional Information"}: ${message || (t.investment?.form?.waMessage?.noInfo || "No additional information provided")}

${t.investment?.form?.waMessage?.closing || "Looking forward to discussing this opportunity with your team."}`
  );
  const waHref = `https://wa.me/971503780091?text=${waText}`;

  return (
    <section className="invest-form-section">
      <motion.h2 className="invest-form-title" initial={{opacity:0,y:16}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:.45}}>
        {title}
      </motion.h2>
      <motion.p className="invest-form-subtitle" initial={{opacity:0,y:16}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:.45,delay:.05}}>
        {subtitle}
      </motion.p>

      <form className="invest-card" onSubmit={handleSubmit} action="https://formsubmit.co/invest@iwinto.com" method="POST">
        {/* Hidden fields for FormSubmit */}
        <input type="hidden" name="_captcha" value="false" />
        <input type="hidden" name="_next" value={window.location.href} />
        <input type="hidden" name="_template" value="table" />
        
        <label className="invest-label">
          <span>{t.investment?.form?.fullName || "Full Name *"}</span>
          <input type="text" name="name" placeholder={t.investment?.form?.fullNamePlaceholder || "Enter your full name"} value={name} onChange={(e)=>setName(e.target.value)} className="invest-input" required />
        </label>

        <label className="invest-label">
          <span>{t.investment?.form?.contactInfo || "Contact Information *"}</span>
          <input type="text" name="contact" placeholder={t.investment?.form?.contactPlaceholder || "Email address or phone number"} value={contact} onChange={(e)=>setContact(e.target.value)} className="invest-input" required />
        </label>

        <div className="invest-row">
          <span className="invest-group-title">{t.investment?.form?.investmentType || "Investment Type *"}</span>
          <div className="invest-segmented">
            <button type="button" className={`invest-seg-item ${type==="normal" ? "active" : ""}`} onClick={()=>setType("normal")}>
              <FaUser className="invest-seg-icon" /> {t.investment?.form?.normalInvestor || "Normal Investor"}
            </button>
            <button type="button" className={`invest-seg-item ${type==="partner" ? "active" : ""}`} onClick={()=>setType("partner")}>
              <FaUserTie className="invest-seg-icon" /> {t.investment?.form?.equityPartner || "Equity Partner"}
            </button>
          </div>
        </div>

        {/* Currency dropdown */}
        <div className="invest-row">
          <span className="invest-group-title center">{t.investment?.form?.preferredCurrency || "Preferred Currency *"}</span>

          <div className="invest-currency-select-wrap">
            <div className="invest-currency-select" ref={ddRef}>
              <button
                type="button"
                className={`invest-currency-trigger ${open ? "open" : ""}`}
                onClick={() => setOpen((o) => !o)}
                aria-haspopup="listbox"
                aria-expanded={open}
              >
                <img
                  src={flagFor(currency, currencies.find(c=>c.code===currency)?.iso)}
                  alt={`${currency} flag`}
                  className="invest-currency-flag"
                  onError={(e) => (e.currentTarget.src = aeFlag)}
                />
                <span className="invest-currency-code">{currency}</span>
              </button>

              {!loading && open && (
                <motion.div
                  className="invest-currency-menu"
                  role="listbox"
                  initial={{ opacity: 0, y: 6, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.15 }}
                >
                  {currencies.map((curr) => (
                    <button
                      type="button"
                      key={`${curr.code}-${curr.iso || "xx"}`}  // unique key
                      className="invest-currency-item"
                      role="option"
                      aria-selected={currency === curr.code}
                      onClick={() => { setCurrency(curr.code); setOpen(false); }}
                      title={curr.name || curr.code}
                    >
                      <img
                        src={flagFor(curr.code, curr.iso)}
                        alt={`${curr.code} flag`}
                        className="invest-currency-flag"
                        onError={(e) => (e.currentTarget.src = aeFlag)}
                      />
                      {/* <span className="invest-item-code">{curr.code}</span> */}
                      {curr.name && <span className="invest-item-name">{curr.name}</span>}
                    </button>
                  ))}
                </motion.div>
              )}
            </div>
          </div>
        </div>

        {/* Amounts (dice layout) */}
        <div className="invest-row">
          <span className="invest-group-title center">{t.investment?.form?.investmentAmount || "Investment Amount *"}</span>

          <div className="invest-dice-layout">
            <div className="invest-dice-row">
              {list.slice(0, 2).map((item) => (
                <AmountCard key={item.id} item={item} currency={currency} active={selectedId===item.id} onSelect={()=>setSelectedId(item.id)} t={t} />
              ))}
            </div>
            <div className="invest-dice-row mid">
              <AmountCard item={list[2]} currency={currency} active={selectedId===list[2].id} onSelect={()=>setSelectedId(list[2].id)} large t={t} />
            </div>
            <div className="invest-dice-row">
              {list.slice(3).map((item) => (
                <AmountCard key={item.id} item={item} currency={currency} active={selectedId===item.id} onSelect={()=>setSelectedId(item.id)} t={t} />
              ))}
            </div>
          </div>
        </div>

        <label className="invest-label">
          <span>{t.investment?.form?.additionalInfo || "Additional Information"}</span>
          <div className="invest-textarea-container">
            <textarea
              name="message"
              rows={4}
              placeholder={t.investment?.form?.messagePlaceholder || "Tell us about your investment goals, timeline, or any specific questions you have..."}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="invest-textarea"
            />
            <FaPencilAlt className="invest-textarea-icon" />
          </div>
        </label>

        <button type="submit" className="invest-btn primary">
          <FaEnvelope className="invest-btn-icon" />
          {t.investment?.form?.submitButton || "Submit Investment Request"}
        </button>

        <div className="invest-divider"><span>{t.investment?.form?.orDivider || "OR"}</span></div>

        <a href={waHref} target="_blank" rel="noreferrer" className="invest-btn whatsapp">
          <FaWhatsapp className="invest-btn-icon" />
          {t.investment?.form?.whatsappButton || "Discuss on WhatsApp"}
        </a>
      </form>
    </section>
  );
};

function AmountCard({ item, currency, active, onSelect, large = false, t }) {
  return (
    <motion.button
      type="button"
      onClick={onSelect}
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.98 }}
      aria-pressed={active}
      className={`invest-amount-card ${active ? "is-active" : ""} ${large ? "is-large" : ""}`}
      style={{ "--accent": item.color }}
    >
      <div className="invest-amount-ico">
        <svg
          className="invest-coin-stack"
          width="24" height="24" viewBox="0 0 24 24" fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="12" cy="14" r="8" fill="currentColor" opacity="0.8"/>
          <circle cx="12" cy="14" r="6" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.3"/>
          <circle cx="12" cy="14" r="4" fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.2"/>
          <circle cx="14" cy="10" r="8" fill="currentColor"/>
          <circle cx="14" cy="10" r="6" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.3"/>
          <circle cx="14" cy="10" r="4" fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.2"/>
          <path d="M6 10 A8 8 0 0 1 14 10 A8 8 0 0 1 6 10 Z" fill="currentColor" opacity="0.6"/>
        </svg>
      </div>

      <div className="invest-amount-number">
        <span className="invest-cur">{currency}</span> {item.value.toLocaleString()}
      </div>

      <div className="invest-amount-meta">
        <span>
          <span className="equity-label">{t.investment?.form?.equityLabel || "Equity"}: {item.equity}%</span>
          <span className="equity-amount">{currency} {Math.round((item.value * item.equity) / 100).toLocaleString()}</span>
        </span>
        <span>
          <span className="return-label">{t.investment?.form?.returnLabel || "Return"}: {item.returns}%</span>
          <span className="return-amount">{currency} {Math.round((item.value * item.returns) / 100).toLocaleString()}</span>
        </span>
      </div>
    </motion.button>
  );
}


export default InvestmentCTA;
